import type { ComponentType } from 'react';
import { ServiceDetailPage } from '@/components/ServiceDetailPage';

export type OfficeRemovalPageConfig = {
  path: string;
  navLabel: string;
  cardTitle: string;
  title: string;
  description: string;
  intro: string;
  highlights: string[];
  process: string[];
  idealFor: string[];
};

function createOfficeRemovalPage(config: OfficeRemovalPageConfig): ComponentType {
  return function OfficeRemovalPage() {
    return (
      <ServiceDetailPage
        activePath={config.path}
        serviceMenuPath="/services/office-removals"
        title={config.title}
        description={config.description}
        intro={config.intro}
        highlights={config.highlights}
        process={config.process}
        idealFor={config.idealFor}
      />
    );
  };
}

const baseHighlights = (location: string): string[] => [
  `Professional office removals planned and delivered across ${location}`,
  'Careful handling for desks, chairs, workstations, IT equipment, and filing',
  'Flexible scheduling including evenings and weekends to reduce downtime',
  'Structured loading plans that protect furniture, technology, and documents',
];

const baseProcess = (location: string): string[] => [
  `We survey your ${location} office, deadlines, inventory, and access requirements.`,
  'A tailored move plan is built around packing zones, loading order, and logistics.',
  'Our team relocates furniture, equipment, and files with clear labelling and care.',
  'Items are placed in the correct rooms at the new premises so your team can settle in fast.',
];

const baseIdealFor = (location: string): string[] => [
  `Businesses relocating offices within or into ${location}`,
  'Companies with desks, chairs, workstations, and IT equipment to move',
  'Teams wanting a structured, low-disruption commercial move',
];

export const officeRemovalPageConfigs: OfficeRemovalPageConfig[] = [
  {
    path: '/services/office-removals/staffordshire',
    navLabel: 'Staffordshire',
    cardTitle: 'Office Removals in Staffordshire',
    title: 'Office Removals in Staffordshire',
    description: 'Professional office removals across Staffordshire from VIP House Movers, designed to keep your business moving with minimal downtime.',
    intro: 'Whether you are moving between towns in Staffordshire or relocating into the county, VIP House Movers delivers a structured office relocation experience with careful planning, dependable transport, and a strong focus on service.',
    highlights: baseHighlights('Staffordshire'),
    process: baseProcess('Staffordshire'),
    idealFor: baseIdealFor('Staffordshire'),
  },
  {
    path: '/services/office-removals/wolverhampton',
    navLabel: 'Wolverhampton',
    cardTitle: 'Office Removals in Wolverhampton',
    title: 'Office Removals in Wolverhampton',
    description: 'Dependable office relocations in Wolverhampton by VIP House Movers with premium care for furniture, IT equipment, and business contents.',
    intro: 'As a Wolverhampton-based removals company, we know local access, parking, and property layouts, which helps us plan smoother office moves for businesses across the city.',
    highlights: baseHighlights('Wolverhampton'),
    process: baseProcess('Wolverhampton'),
    idealFor: baseIdealFor('Wolverhampton'),
  },
  {
    path: '/services/office-removals/birmingham',
    navLabel: 'Birmingham',
    cardTitle: 'Office Removals in Birmingham',
    title: 'Office Removals in Birmingham',
    description: 'Organised office removals in Birmingham from VIP House Movers, built around busy city access, loading restrictions, and tight schedules.',
    intro: 'Birmingham office moves often involve timed loading bays, lift access, and one-way systems, so we plan each relocation carefully to keep your business running smoothly through the transition.',
    highlights: baseHighlights('Birmingham'),
    process: baseProcess('Birmingham'),
    idealFor: baseIdealFor('Birmingham'),
  },
  {
    path: '/services/office-removals/walsall',
    navLabel: 'Walsall',
    cardTitle: 'Office Removals in Walsall',
    title: 'Office Removals in Walsall',
    description: 'VIP House Movers handles Walsall office relocations with efficient planning, careful transport, and professional crews focused on reducing downtime.',
    intro: 'From small studios to multi-floor offices, we support Walsall businesses with a structured moving service that protects your equipment and keeps your team productive.',
    highlights: baseHighlights('Walsall'),
    process: baseProcess('Walsall'),
    idealFor: baseIdealFor('Walsall'),
  },
  {
    path: '/services/office-removals/cannock',
    navLabel: 'Cannock',
    cardTitle: 'Office Removals in Cannock',
    title: 'Office Removals in Cannock',
    description: 'Professional office removals in Cannock by VIP House Movers, with tailored plans for desks, IT, files, and specialist business equipment.',
    intro: 'We help Cannock businesses relocate offices with confidence by combining clear planning, respectful handling, and reliable transport from collection to setup.',
    highlights: baseHighlights('Cannock'),
    process: baseProcess('Cannock'),
    idealFor: baseIdealFor('Cannock'),
  },
  {
    path: '/services/office-removals/dudley',
    navLabel: 'Dudley',
    cardTitle: 'Office Removals in Dudley',
    title: 'Office Removals in Dudley',
    description: 'Premium office removals in Dudley from VIP House Movers, helping businesses relocate workspaces with minimal disruption and maximum care.',
    intro: 'Whether you are upgrading premises, downsizing, or consolidating sites, our Dudley office removals service keeps your relocation organised, efficient, and professionally delivered.',
    highlights: baseHighlights('Dudley'),
    process: baseProcess('Dudley'),
    idealFor: baseIdealFor('Dudley'),
  },
  {
    path: '/services/office-removals/west-bromwich',
    navLabel: 'West Bromwich',
    cardTitle: 'Office Removals in West Bromwich',
    title: 'Office Removals in West Bromwich',
    description: 'Efficient office removals in West Bromwich by VIP House Movers, with careful transport, planned logistics, and polished customer service.',
    intro: 'West Bromwich office moves can involve urban access, shared loading areas, and strict timing, so we plan each stage carefully to deliver a smooth commercial relocation.',
    highlights: baseHighlights('West Bromwich'),
    process: baseProcess('West Bromwich'),
    idealFor: baseIdealFor('West Bromwich'),
  },
  {
    path: '/services/office-removals/telford',
    navLabel: 'Telford',
    cardTitle: 'Office Removals in Telford',
    title: 'Office Removals in Telford',
    description: 'VIP House Movers provides organised office removals in Telford, with flexible scheduling and premium care for business furniture and equipment.',
    intro: 'Our Telford office removals service helps businesses relocate efficiently with practical planning, experienced crews, and clear communication at every stage.',
    highlights: baseHighlights('Telford'),
    process: baseProcess('Telford'),
    idealFor: baseIdealFor('Telford'),
  },
  {
    path: '/services/office-removals/lichfield',
    navLabel: 'Lichfield',
    cardTitle: 'Office Removals in Lichfield',
    title: 'Office Removals in Lichfield',
    description: 'Premium office relocations in Lichfield from VIP House Movers, combining careful handling, dependable transport, and polished customer care.',
    intro: 'From heritage buildings to modern business parks, we tailor each Lichfield office move around access, timing, and the specific needs of your workplace.',
    highlights: baseHighlights('Lichfield'),
    process: baseProcess('Lichfield'),
    idealFor: baseIdealFor('Lichfield'),
  },
  {
    path: '/services/office-removals/tamworth',
    navLabel: 'Tamworth',
    cardTitle: 'Office Removals in Tamworth',
    title: 'Office Removals in Tamworth',
    description: 'Reliable office removals in Tamworth by VIP House Movers, with structured planning and professional crews focused on reducing disruption.',
    intro: 'We help Tamworth businesses move offices with a polished service that protects your furniture, equipment, and productivity from start to finish.',
    highlights: baseHighlights('Tamworth'),
    process: baseProcess('Tamworth'),
    idealFor: baseIdealFor('Tamworth'),
  },
];

export const officeRemovalPages = officeRemovalPageConfigs.map((config) => ({
  ...config,
  component: createOfficeRemovalPage(config),
}));
